import type { EventDataDto } from "../../../../autogen/bff-api";
import type { IconType } from "react-icons";
import {
  FaBalanceScale,
  FaBoxOpen,
  FaClipboardCheck,
  FaClipboardList,
  FaFileAlt,
  FaGavel,
  FaInfoCircle,
  FaTags,
  FaUserCheck,
  FaUserPlus,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { ProcedureType } from "../../../../autogen/bff-api";
import { useProcurementTerms } from "../../../../common/terminology/useProcurementTerms";

export const WIZARD_STEPS = [
  { key: "info", icon: FaInfoCircle },
  { key: "classification", icon: FaTags },
  { key: "procedure", icon: FaGavel },
  { key: "qualification", icon: FaUserCheck },
  { key: "awardCriteria", icon: FaBalanceScale },
  { key: "minimumRequirements", icon: FaClipboardCheck },
  { key: "documents", icon: FaFileAlt },
  { key: "summary", icon: FaClipboardList },
  { key: "details", icon: FaClipboardList },
  { key: "products", icon: FaBoxOpen },
  { key: "invitations", icon: FaUserPlus },
  { key: "reviewAndPublish", icon: FaClipboardCheck },
] as const satisfies ReadonlyArray<{ key: string; icon: IconType }>;

export type PublicEventStep = (typeof WIZARD_STEPS)[number]["key"];

export const WIZARD_STEP_KEYS = WIZARD_STEPS.map((s) => s.key) as readonly PublicEventStep[];

export type WizardStep = (typeof WIZARD_STEPS)[number];

export interface ProcessDefinition {
  key: "publicOpen" | "private";
  steps: ReadonlyArray<WizardStep>;
}

export interface OrgWizardAccess {
  hasPublicProcurementAccess: boolean;
  hasProductAccess: boolean;
}

const step = (key: PublicEventStep): WizardStep => {
  const found = WIZARD_STEPS.find((s) => s.key === key);
  if (!found) throw new Error(`Unknown wizard step: ${key}`);
  return found;
};

const PUBLIC_STEPS: readonly PublicEventStep[] = [
  "info",
  "classification",
  "procedure",
  "qualification",
  "awardCriteria",
  "minimumRequirements",
  "documents",
  "summary",
];

const publicProcess = (keys: readonly PublicEventStep[]): ProcessDefinition => ({
  key: "publicOpen",
  steps: keys.map(step),
});

const PUBLIC_OPEN = publicProcess(PUBLIC_STEPS);

/**
 * One row per procedure whose run differs from the standard public one, like ProcedureRules and
 * QualificationPolicy on the BFF. A procedure with no row runs [PUBLIC_OPEN].
 *
 * FOA § 26-7: a purchase runs among suppliers already admitted to the system, so its qualification
 * requirements were settled at admission and are not asked again.
 */
const PUBLIC_PROCESS_BY_PROCEDURE: Partial<Record<ProcedureType, ProcessDefinition>> = {
  [ProcedureType.DpsCompetition]: publicProcess(PUBLIC_STEPS.filter((key) => key !== "qualification")),
};

/**
 * Which optional parts a private procurement asks for. The buyer switches these on in the first
 * step, and each one that is on contributes its step to the process.
 */
export interface EventInclusions {
  products: boolean;
  minimumRequirements: boolean;
  awardCriteria: boolean;
}

export const ALL_INCLUSIONS: EventInclusions = {
  products: true,
  minimumRequirements: true,
  awardCriteria: true,
};

/**
 * A new event starts with every optional part switched off and the buyer opts in, so a stored flag
 * is the answer whenever it is set. An event created before the toggles has no flag and is read by
 * what it already holds — otherwise introducing the toggles would hide krav somebody had written.
 * Products live in their own aggregate and can't be counted from here, so a flagless event keeps
 * the step it has always had.
 */
export const includesAwardCriteria = (eventData: EventDataDto): boolean =>
  eventData.includeAwardCriteria ?? eventData.awardCriteria != null;

export const includesMinimumRequirements = (eventData: EventDataDto): boolean =>
  eventData.includeMinimumRequirements ?? (eventData.minimumRequirements?.length ?? 0) > 0;

export const resolveInclusions = (eventData: EventDataDto): EventInclusions => ({
  products: eventData.includeProducts ?? true,
  minimumRequirements: includesMinimumRequirements(eventData),
  awardCriteria: includesAwardCriteria(eventData),
});

const privateProcess = (hasProductAccess: boolean, inclusions: EventInclusions): ProcessDefinition => ({
  key: "private",
  steps: (
    [
      "info",
      "details",
      ...(hasProductAccess && inclusions.products ? (["products"] as const) : []),
      ...(inclusions.awardCriteria ? (["awardCriteria"] as const) : []),
      ...(inclusions.minimumRequirements ? (["minimumRequirements"] as const) : []),
      "documents",
      "invitations",
      "reviewAndPublish",
    ] as PublicEventStep[]
  ).map((key) => step(key)),
});

/** The two things that narrow a process: which procedure runs it, and which optional parts it includes. */
export interface ProcessRefinements {
  procedureType?: ProcedureType | null;
  inclusions?: EventInclusions;
}

export const resolveProcess = (
  contextType: "Unclassified" | "Public" | "Private",
  access: OrgWizardAccess,
  { procedureType, inclusions = ALL_INCLUSIONS }: ProcessRefinements = {},
): ProcessDefinition | null => {
  if (contextType === "Public") {
    return (procedureType && PUBLIC_PROCESS_BY_PROCEDURE[procedureType]) ?? PUBLIC_OPEN;
  }
  if (contextType === "Private") return privateProcess(access.hasProductAccess, inclusions);
  return access.hasPublicProcurementAccess ? null : privateProcess(access.hasProductAccess, inclusions);
};

export const useStepLabel = (): ((key: PublicEventStep) => string) => {
  const { t } = useTranslation();
  const terms = useProcurementTerms();
  return (key) => {
    switch (key) {
      case "info":
        return t("Basic information");
      case "classification":
        return t("Classification and contract");
      case "procedure":
        return t("Procedure");
      case "qualification":
        return t("Qualification requirements");
      case "awardCriteria":
        return terms.awardCriteria;
      case "minimumRequirements":
        return t("Minimum requirements");
      case "documents":
        return t("Documents");
      case "summary":
        return t("Summary");
      case "details":
        return t("Details");
      case "products":
        return t("Products");
      case "invitations":
        return t("Invitations");
      case "reviewAndPublish":
        return t("Review and publish");
    }
  };
};

/** Short labels for the horizontal process bar (full labels are kept for buttons + the mobile drawer). */
export const useStepShortLabel = (): ((key: PublicEventStep) => string) => {
  const { t } = useTranslation();
  const terms = useProcurementTerms();
  return (key) => {
    switch (key) {
      case "info":
        return t("Basics");
      case "classification":
        return t("Classification");
      case "procedure":
        return t("Procedure");
      case "qualification":
        return t("Qualification");
      case "awardCriteria":
        return terms.awardCriteria;
      case "minimumRequirements":
        return t("Minimum requirements");
      case "documents":
        return t("Documents");
      case "summary":
        return t("Summary");
      case "details":
        return t("Details");
      case "products":
        return t("Products");
      case "invitations":
        return t("Invitations");
      case "reviewAndPublish":
        return t("Publish");
    }
  };
};

export const stepNeighbors = (
  current: PublicEventStep,
  steps: ReadonlyArray<WizardStep>,
): { previous: PublicEventStep | undefined; next: PublicEventStep | undefined } => {
  const keys: readonly PublicEventStep[] = steps.map((s) => s.key);
  const idx = keys.indexOf(current);
  return {
    previous: idx > 0 ? keys[idx - 1] : undefined,
    next: idx >= 0 && idx < keys.length - 1 ? keys[idx + 1] : undefined,
  };
};
